import { captureException } from "@sentry/react"

import { getFlag } from "../flags/ff-cache"

/**
 * Checks if an error is a quota exceeded error for localStorage/sessionStorage
 * @param err - The error to check
 * @returns True if the error indicates storage quota has been exceeded
 */
const isQuotaExceededError = (err: unknown): boolean => {
    return (
        err instanceof DOMException && (
            err.name === "QuotaExceededError" ||
            err.name === "NS_ERROR_DOM_QUOTA_REACHED"
        )
    )
}

/**
 * Safely sets an item in browser storage with quota exceeded error handling
 */
export const setStorage = async (storageType: Storage, key: string, value: string) => {
    try {
        storageType.setItem(key, value)
    } catch (err) {
        if (isQuotaExceededError(err)) {
            // Report quota exceeded to Sentry
            captureException(err, {
                tags: {
                    errorType: 'storage_quota_exceeded',
                    storageType: storageType === localStorage ? 'localStorage' : 'sessionStorage'
                },
                extra: { key }
            })
            // Clear the item to prevent further quota issues
            storageType.setItem(key, '')

            const isScoreRebrand =
                getFlag('feature.score_rebrand', false) || getFlag('feature.score_rebrand_cp', false)
            const brand = isScoreRebrand ? 'Score.' : 'Acre'
            throw new Error(`Browser ${storageType === localStorage ? "local" : "session"} storage is full. Please clear your browsing data and reload. If the problem persists, contact ${brand} support.`)
        } else {
            // Clear the item to prevent further quota issues
            storageType.setItem(key, '')
            console.error('LocalStorage error:', err)

            // Report other storage errors to Sentry
            captureException(err, {
                tags: {
                    errorType: 'storage_error',
                    storageType: storageType === localStorage ? 'localStorage' : 'sessionStorage'
                },
                extra: { key }
            })
            throw err
        }
    }
}
